/**
 * The 12 elemental types and the v0 type chart. See docs/03-creatures-types-and-moves.md.
 */

export const ELEMENT_TYPES = [
  'flame',
  'tide',
  'verdant',
  'volt',
  'frost',
  'stone',
  'gale',
  'venom',
  'mind',
  'shade',
  'iron',
  'brawl',
] as const;

export type ElementType = (typeof ELEMENT_TYPES)[number];

export const SUPER_EFFECTIVE_MULTIPLIER = 1.5;
export const RESISTED_MULTIPLIER = 2 / 3;

/** Attacking type -> defending types it is super effective against. */
export const SUPER_EFFECTIVE: Readonly<Record<ElementType, readonly ElementType[]>> = {
  flame: ['verdant', 'frost', 'iron'],
  tide: ['flame', 'stone'],
  verdant: ['tide', 'stone'],
  volt: ['tide', 'gale', 'iron'],
  frost: ['verdant', 'gale'],
  stone: ['flame', 'volt', 'gale', 'frost'],
  gale: ['verdant', 'brawl', 'venom'],
  venom: ['verdant', 'tide'],
  mind: ['brawl', 'venom'],
  shade: ['mind', 'shade'],
  iron: ['frost', 'stone'],
  brawl: ['stone', 'iron', 'frost', 'shade'],
};

/** Attacking type -> defending types that resist it. */
export const RESISTED_BY: Readonly<Record<ElementType, readonly ElementType[]>> = {
  flame: ['tide', 'stone', 'flame'],
  tide: ['tide', 'verdant', 'frost'],
  verdant: ['flame', 'gale', 'venom', 'iron', 'verdant'],
  volt: ['stone', 'verdant', 'volt'],
  frost: ['flame', 'iron', 'frost'],
  stone: ['brawl', 'iron'],
  gale: ['stone', 'volt', 'iron'],
  venom: ['stone', 'iron', 'venom', 'shade'],
  mind: ['iron', 'mind', 'shade'],
  shade: ['flame', 'brawl'],
  iron: ['flame', 'tide', 'volt', 'iron'],
  brawl: ['gale', 'mind', 'venom'],
};

/** Multiplier for one attacking type against one defending type. There are no immunities. */
export function singleTypeMultiplier(attack: ElementType, defend: ElementType): number {
  if (SUPER_EFFECTIVE[attack].includes(defend)) return SUPER_EFFECTIVE_MULTIPLIER;
  if (RESISTED_BY[attack].includes(defend)) return RESISTED_MULTIPLIER;
  return 1;
}

/** Multiplier against a (possibly dual-typed) defender: the product over its types. */
export function typeMultiplier(attack: ElementType, defenders: readonly ElementType[]): number {
  let m = 1;
  for (const d of defenders) m *= singleTypeMultiplier(attack, d);
  return m;
}
